import type { ReactNode } from 'react';
import { cx } from '../cx';
import { Check, Warning } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { TONE_SOFT, TONE_SOLID, type Tone } from '../tone';

/** Positive (shares the selection green) and attention (ink) carry a glyph so they never rely on hue. */
const GLYPH = { positive: Check, attention: Warning } as const;

export type BadgeProps = {
  /** The shared status vocabulary: neutral (New, Top employer), positive (a match level). */
  tone?: Tone;
  /** Solid fill (default) to stand out on a card; soft for a quieter marker (a partial match). */
  solid?: boolean;
  children: ReactNode;
  className?: string;
};

/**
 * A compact semibold marker for one fact on a card: New (posted within 7 days, not "Seen"), a match
 * level, a highlight. At most one per card; never sun (yellow is for action).
 */
export function Badge({ tone = 'neutral', solid = true, children, className }: BadgeProps) {
  return (
    <span
      className={cx(
        'inline-flex h-6 items-center gap-1 rounded-jc-info px-2.5 text-xs font-semibold whitespace-nowrap',
        solid ? TONE_SOLID[tone] : TONE_SOFT[tone],
        className,
      )}
    >
      {tone === 'positive' || tone === 'attention' ? <Icon icon={GLYPH[tone]} size="sm" /> : null}
      {children}
    </span>
  );
}
